import { useInterfaceThemeColorToken } from '@unifyapps/defs/hooks/useInterfaceStyles';
import { useUtilityVariableFromClassName } from '@unifyapps/defs/hooks/useUtilityVariableFromClassName';
import type { LCColorValue } from '@unifyapps/defs/types/styleObject';
import { useMemo } from 'react';
import { getColorClassNameAndValue } from '../../ColorPicker/utils/getColorClassNameAndValue';

/**
 * Resolves color values from various input formats to actual color values
 * Handles utility class names, CSS variables, and direct color values
 */
export const useResolvedColor = (color: string | LCColorValue | undefined, fallback: string) => {
  // Extract className for LCColorValue objects
  const className = useMemo(() => {
    if (!color || typeof color === 'string') return color;
    const { className, value } = getColorClassNameAndValue(color);
    return className ?? value ?? '';
  }, [color]);

  // Always call hooks at the top level
  const {
    variable: { isClassName, value: colorVariable },
  } = useUtilityVariableFromClassName(className);

  const colorToken = useInterfaceThemeColorToken({ color: colorVariable });

  return useMemo(() => {
    if (!color) return fallback;

    return isClassName ? (colorToken ?? fallback) : colorVariable || fallback;
  }, [color, fallback, isClassName, colorToken, colorVariable]);
};
